/* eslint-disable */
import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { DOC_TYPES } from "./DocumentsCenter";
import SendDocEmailModal from "@/components/SendDocEmailModal";
import { ArrowLeft, Save, FileDown, FileSpreadsheet, Plus, Trash2, Loader2, Sparkles, Mail } from "lucide-react";

const todayISO = () => new Date().toISOString().slice(0, 10);
const daysInMonth = (y, m) => new Date(y, m, 0).getDate();

function defaultData(type) {
  const d = new Date();
  if (type === "pontaj") {
    return {
      site_name: "", period_label: `${d.toLocaleDateString("en", { month: "long" })} ${d.getFullYear()}`,
      period_month: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
      days_count: daysInMonth(d.getFullYear(), d.getMonth() + 1),
      employees: [{ name: "", role: "", hours: [] }],
    };
  }
  if (type === "proces_verbal")
    return { number: "", date: todayISO(), beneficiary: "", contractor: "", project: "", location: "", works: [{ description: "", qty: "", unit: "buc", observations: "" }], conclusions: "" };
  if (type === "bon_consum")
    return { number: "", date: todayISO(), site_name: "", received_by: "", materials: [{ name: "", unit: "buc", qty: "", notes: "" }] };
  return { number: "", date: todayISO(), client: "", location: "", team: "", hours: "", description: "", materials_note: "" };
}

export default function DocumentEditor() {
  const { t, i18n } = useTranslation();
  const { type, id } = useParams();
  const nav = useNavigate();
  const [docType, setDocType] = useState(type || "fisa_lucru");
  const [docId, setDocId] = useState(id || null);
  const [title, setTitle] = useState("");
  const [data, setData] = useState(() => defaultData(type || "fisa_lucru"));
  const [signatures, setSignatures] = useState([
    { label: "Reprezentant firmă", name: "" }, { label: "Beneficiar", name: "" }, { label: "Șef șantier", name: "" },
  ]);
  const [loading, setLoading] = useState(!!id);
  const [busy, setBusy] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);

  const meta = useMemo(() => DOC_TYPES.find((x) => x.id === docType) || DOC_TYPES[3], [docType]);
  const defaultTitle = (ty) => `${t(`docs.type_${ty}`)} — ${new Date().toLocaleDateString(i18n.language)}`;

  useEffect(() => {
    if (!id) { setTitle(defaultTitle(type)); return; }
    (async () => {
      try {
        const r = await api.get(`/documents/${id}`);
        setDocType(r.data.doc_type); setTitle(r.data.title);
        setData({ ...defaultData(r.data.doc_type), ...(r.data.data || {}) });
        if (r.data.signatures?.length) setSignatures(r.data.signatures);
      } catch { toast.error(t("docs.notFound")); nav("/app/documente"); }
      finally { setLoading(false); }
    })();
  }, [id]);

  const save = async () => {
    setBusy(true);
    const payload = { doc_type: docType, title, data, signatures };
    try {
      let res;
      if (docId) res = await api.put(`/documents/${docId}`, payload);
      else { res = await api.post("/documents", payload); setDocId(res.data.id); nav(`/app/documente/${res.data.id}`, { replace: true }); }
      toast.success(t("docs.saved"));
      return res.data.id || docId;
    } catch (e) { toast.error(e.response?.data?.detail || t("docs.saveError")); return null; }
    finally { setBusy(false); }
  };

  const exportDoc = async (fmt) => {
    let realId = docId;
    if (!realId) { realId = await save(); if (!realId) return; }
    else { await save(); }
    try {
      const r = await api.get(`/documents/${realId}/export?fmt=${fmt}&lang=${i18n.language}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${(title || "document").replace(/\s+/g, "_")}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error(t("docs.exportError")); }
  };

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const setSig = (i, k, v) => setSignatures((s) => s.map((x, idx) => idx === i ? { ...x, [k]: v } : x));

  if (loading) return <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-[#00BFFF]" /></div>;

  return (
    <div className="p-5 lg:p-10 max-w-5xl mx-auto" data-testid="document-editor">
      <button onClick={() => nav("/app/documente")} className="text-sm text-[#94A3B8] hover:text-[#E2E8F0] flex items-center gap-1.5" data-testid="back-btn">
        <ArrowLeft size={15} /> {t("docs.back")}
      </button>

      <div className="flex items-start justify-between flex-wrap gap-4 mt-4">
        <div className="flex-1 min-w-[240px]">
          <div className="overline text-[#00BFFF] flex items-center gap-2"><meta.icon size={14} /> {t(`docs.type_${docType}`)}</div>
          <input className="input-flat mt-2 font-display font-bold text-lg" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t("docs.titlePh")} data-testid="doc-title" />
        </div>
        <div className="flex items-center gap-2">
          <button onClick={save} disabled={busy} className="btn-primary flex items-center gap-2" data-testid="save-btn">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} {t("docs.save")}
          </button>
          <button onClick={() => exportDoc("pdf")} className="btn-outline flex items-center gap-2" data-testid="export-pdf-btn"><FileDown size={16} /> PDF</button>
          <button onClick={() => exportDoc("xlsx")} className="btn-outline flex items-center gap-2" data-testid="export-excel-btn"><FileSpreadsheet size={16} /> Excel</button>
          <button onClick={async () => { const rid = await save(); if (rid) setEmailOpen(true); }} className="btn-outline flex items-center gap-2" data-testid="send-email-btn"><Mail size={16} /> {t("docs.email")}</button>
        </div>
      </div>

      <div className="card-flat p-5 mt-6">
        {docType === "pontaj" && <PontajForm data={data} set={set} setData={setData} />}
        {docType === "proces_verbal" && <ProcesVerbalForm data={data} set={set} setData={setData} />}
        {docType === "bon_consum" && <BonConsumForm data={data} set={set} setData={setData} />}
        {docType === "fisa_lucru" && <FisaLucruForm data={data} set={set} />}
      </div>

      <div className="mt-6">
        <div className="overline mb-3">{t("docs.signaturesTitle")}</div>
        <div className="grid sm:grid-cols-3 gap-3">
          {signatures.map((s, i) => (
            <div key={i} className="card-flat p-4" data-testid={`sig-${i}`}>
              <label className="text-xs text-[#94A3B8]">{t("docs.sigLabel", { n: i + 1 })}</label>
              <input className="input-flat mt-1" value={s.label} onChange={(e) => setSig(i, "label", e.target.value)} placeholder={t("docs.sigLabelPh")} data-testid={`sig-label-${i}`} />
              <label className="text-xs text-[#94A3B8] mt-3 block">{t("docs.sigName")}</label>
              <input className="input-flat mt-1" value={s.name} onChange={(e) => setSig(i, "name", e.target.value)} placeholder={t("docs.sigNamePh")} data-testid={`sig-name-${i}`} />
            </div>
          ))}
        </div>
        <p className="text-xs text-[#5B6B82] mt-2">{t("docs.signaturesHint")}</p>
      </div>

      {emailOpen && <SendDocEmailModal doc={{ id: docId, title }} onClose={() => setEmailOpen(false)} />}
    </div>
  );
}

const Field = ({ label, children }) => (
  <div><label className="overline block mb-2">{label}</label>{children}</div>
);

function AIKeywordBox({ docType, onResult, placeholder }) {
  const { t, i18n } = useTranslation();
  const [kw, setKw] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (kw.trim().length < 3) return toast.error(t("docs.aiKeywords"));
    setBusy(true);
    try {
      const r = await api.post("/ai/document-assist", { doc_type: docType, keywords: kw.trim(), lang: i18n.language });
      onResult(r.data); toast.success(t("docs.aiGenerated"));
    } catch (e) { toast.error(e.response?.data?.detail || t("docs.aiError")); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-lg border border-[#1E293B] bg-[#0B111D] p-3" data-testid="ai-assist-box">
      <div className="flex items-center gap-2 text-xs font-semibold text-[#00BFFF] mb-2"><Sparkles size={14} /> {t("docs.aiBoxTitle")}</div>
      <div className="flex flex-col sm:flex-row gap-2">
        <input className="input-flat flex-1" value={kw} onChange={(e) => setKw(e.target.value)} placeholder={placeholder} data-testid="ai-assist-input"
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); run(); } }} />
        <button type="button" onClick={run} disabled={busy} className="btn-primary flex items-center justify-center gap-2 shrink-0" data-testid="ai-assist-btn">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} {t("docs.aiGenerate")}
        </button>
      </div>
    </div>
  );
}

function PontajForm({ data, set, setData }) {
  const { t } = useTranslation();
  const days = Number(data.days_count) || 30;
  const onMonth = (v) => {
    const [y, m] = v.split("-").map(Number);
    const label = new Date(y, m - 1, 1).toLocaleDateString("en", { month: "long", year: "numeric" });
    setData((d) => ({ ...d, period_month: v, period_label: label, days_count: daysInMonth(y, m) }));
  };
  const setHour = (ei, di, val) => setData((d) => {
    const emps = d.employees.map((e, idx) => {
      if (idx !== ei) return e;
      const hours = [...(e.hours || [])];
      while (hours.length < days) hours.push("");
      hours[di] = val;
      return { ...e, hours };
    });
    return { ...d, employees: emps };
  });
  const setEmp = (ei, k, v) => setData((d) => ({ ...d, employees: d.employees.map((e, idx) => idx === ei ? { ...e, [k]: v } : e) }));
  const addEmp = () => setData((d) => ({ ...d, employees: [...d.employees, { name: "", role: "", hours: [] }] }));
  const delEmp = (ei) => setData((d) => ({ ...d, employees: d.employees.filter((_, idx) => idx !== ei) }));
  const total = (e) => (e.hours || []).reduce((a, h) => a + (parseFloat(h) || 0), 0);

  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-3 gap-4">
        <Field label={t("docs.site")}><input className="input-flat" value={data.site_name} onChange={(e) => set("site_name", e.target.value)} placeholder={t("docs.sitePh")} data-testid="pontaj-site" /></Field>
        <Field label={t("docs.month")}><input type="month" className="input-flat" value={data.period_month} onChange={(e) => onMonth(e.target.value)} data-testid="pontaj-month" /></Field>
        <Field label={t("docs.daysCount")}><input type="number" min="1" max="31" className="input-flat" value={data.days_count} onChange={(e) => set("days_count", Number(e.target.value))} data-testid="pontaj-days" /></Field>
      </div>

      <div className="overflow-x-auto border border-[#1E293B] rounded-lg">
        <table className="text-xs min-w-max" data-testid="pontaj-grid">
          <thead>
            <tr className="bg-[#111C30] text-[#94A3B8]">
              <th className="p-2 text-left sticky left-0 bg-[#111C30] z-10 min-w-[160px]">{t("docs.empNamePh")}</th>
              {Array.from({ length: days }, (_, i) => <th key={i} className="p-1.5 text-center w-9 font-mono-plex">{i + 1}</th>)}
              <th className="p-2 text-center min-w-[52px]">{t("docs.total")}</th>
              <th className="p-1 w-8"></th>
            </tr>
          </thead>
          <tbody>
            {data.employees.map((e, ei) => (
              <tr key={ei} className="border-t border-[#1E293B]">
                <td className="p-1.5 sticky left-0 bg-[#0B111D] z-10 min-w-[160px]">
                  <input className="input-flat !py-1 !text-xs" value={e.name} onChange={(ev) => setEmp(ei, "name", ev.target.value)} placeholder={t("docs.empNamePh")} data-testid={`emp-name-${ei}`} />
                  <input className="input-flat !py-1 !text-xs mt-1" value={e.role} onChange={(ev) => setEmp(ei, "role", ev.target.value)} placeholder={t("docs.empRolePh")} data-testid={`emp-role-${ei}`} />
                </td>
                {Array.from({ length: days }, (_, di) => (
                  <td key={di} className="p-0.5">
                    <input className="w-8 h-8 text-center bg-transparent border border-[#1E293B] rounded text-xs focus:border-[#00BFFF] outline-none" value={(e.hours || [])[di] ?? ""} onChange={(ev) => setHour(ei, di, ev.target.value)} data-testid={`emp-${ei}-day-${di}`} />
                  </td>
                ))}
                <td className="p-2 text-center font-bold text-[#00BFFF]">{total(e) || ""}</td>
                <td className="p-1"><button onClick={() => delEmp(ei)} className="text-[#EF4444] hover:opacity-70" data-testid={`emp-del-${ei}`}><Trash2 size={14} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button onClick={addEmp} className="btn-outline flex items-center gap-2 text-sm" data-testid="add-employee"><Plus size={15} /> {t("docs.addEmployee")}</button>
    </div>
  );
}

function RowList({ items, columns, onChange, onAdd, onDel, addLabel, testid }) {
  return (
    <div className="space-y-3">
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={i} className="grid gap-2 items-start" style={{ gridTemplateColumns: columns.map((c) => c.w).join(" ") + " 36px" }} data-testid={`${testid}-row-${i}`}>
            {columns.map((c) => (
              c.type === "textarea"
                ? <textarea key={c.k} rows={1} className="input-flat !py-2" value={it[c.k] ?? ""} onChange={(e) => onChange(i, c.k, e.target.value)} placeholder={c.ph} data-testid={`${testid}-${c.k}-${i}`} />
                : <input key={c.k} className="input-flat !py-2" value={it[c.k] ?? ""} onChange={(e) => onChange(i, c.k, e.target.value)} placeholder={c.ph} data-testid={`${testid}-${c.k}-${i}`} />
            ))}
            <button onClick={() => onDel(i)} className="btn-ink p-2 text-[#EF4444] mt-0.5" data-testid={`${testid}-del-${i}`}><Trash2 size={15} /></button>
          </div>
        ))}
      </div>
      <button onClick={onAdd} className="btn-outline flex items-center gap-2 text-sm" data-testid={`${testid}-add`}><Plus size={15} /> {addLabel}</button>
    </div>
  );
}

function ProcesVerbalForm({ data, set, setData }) {
  const { t } = useTranslation();
  const cols = [
    { k: "description", ph: t("docs.workDescPh"), w: "1fr", type: "textarea" },
    { k: "qty", ph: t("docs.qty"), w: "70px" }, { k: "unit", ph: t("docs.unit"), w: "70px" },
    { k: "observations", ph: t("docs.observations"), w: "1fr" },
  ];
  const onChange = (i, k, v) => setData((d) => ({ ...d, works: d.works.map((w, idx) => idx === i ? { ...w, [k]: v } : w) }));
  return (
    <div className="space-y-4">
      <AIKeywordBox docType="proces_verbal" placeholder={t("docs.aiPhPV")}
        onResult={(d) => setData((prev) => ({
          ...prev,
          works: d.works && d.works.length ? d.works : prev.works,
          conclusions: d.conclusions || prev.conclusions,
        }))} />
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label={t("docs.number")}><input className="input-flat" value={data.number} onChange={(e) => set("number", e.target.value)} data-testid="pv-number" /></Field>
        <Field label={t("docs.date")}><input type="date" className="input-flat" value={data.date} onChange={(e) => set("date", e.target.value)} data-testid="pv-date" /></Field>
        <Field label={t("docs.beneficiary")}><input className="input-flat" value={data.beneficiary} onChange={(e) => set("beneficiary", e.target.value)} data-testid="pv-beneficiary" /></Field>
        <Field label={t("docs.contractor")}><input className="input-flat" value={data.contractor} onChange={(e) => set("contractor", e.target.value)} data-testid="pv-contractor" /></Field>
        <Field label={t("docs.project")}><input className="input-flat" value={data.project} onChange={(e) => set("project", e.target.value)} data-testid="pv-project" /></Field>
        <Field label={t("docs.location")}><input className="input-flat" value={data.location} onChange={(e) => set("location", e.target.value)} data-testid="pv-location" /></Field>
      </div>
      <Field label={t("docs.worksReceived")}>
        <RowList items={data.works} columns={cols} onChange={onChange} addLabel={t("docs.addWork")} testid="pv-work"
          onAdd={() => setData((d) => ({ ...d, works: [...d.works, { description: "", qty: "", unit: "buc", observations: "" }] }))}
          onDel={(i) => setData((d) => ({ ...d, works: d.works.filter((_, idx) => idx !== i) }))} />
      </Field>
      <Field label={t("docs.conclusions")}><textarea rows={3} className="input-flat" value={data.conclusions} onChange={(e) => set("conclusions", e.target.value)} placeholder={t("docs.conclusionsPh")} data-testid="pv-conclusions" /></Field>
    </div>
  );
}

function BonConsumForm({ data, set, setData }) {
  const { t } = useTranslation();
  const cols = [
    { k: "name", ph: t("docs.materialNamePh"), w: "1fr" }, { k: "unit", ph: t("docs.unit"), w: "80px" },
    { k: "qty", ph: t("docs.qty"), w: "90px" }, { k: "notes", ph: t("docs.observations"), w: "1fr" },
  ];
  const onChange = (i, k, v) => setData((d) => ({ ...d, materials: d.materials.map((m, idx) => idx === i ? { ...m, [k]: v } : m) }));
  return (
    <div className="space-y-4">
      <AIKeywordBox docType="bon_consum" placeholder={t("docs.aiPhBC")}
        onResult={(d) => setData((prev) => ({ ...prev, materials: d.materials && d.materials.length ? d.materials : prev.materials }))} />
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label={t("docs.number")}><input className="input-flat" value={data.number} onChange={(e) => set("number", e.target.value)} data-testid="bc-number" /></Field>
        <Field label={t("docs.date")}><input type="date" className="input-flat" value={data.date} onChange={(e) => set("date", e.target.value)} data-testid="bc-date" /></Field>
        <Field label={t("docs.siteWork") || t("docs.site")}><input className="input-flat" value={data.site_name} onChange={(e) => set("site_name", e.target.value)} data-testid="bc-site" /></Field>
        <Field label={t("docs.receivedBy")}><input className="input-flat" value={data.received_by} onChange={(e) => set("received_by", e.target.value)} data-testid="bc-received" /></Field>
      </div>
      <Field label={t("docs.materialsConsumed")}>
        <RowList items={data.materials} columns={cols} onChange={onChange} addLabel={t("docs.addMaterial")} testid="bc-mat"
          onAdd={() => setData((d) => ({ ...d, materials: [...d.materials, { name: "", unit: "buc", qty: "", notes: "" }] }))}
          onDel={(i) => setData((d) => ({ ...d, materials: d.materials.filter((_, idx) => idx !== i) }))} />
      </Field>
    </div>
  );
}

function FisaLucruForm({ data, set }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-4">
      <AIKeywordBox docType="fisa_lucru" placeholder={t("docs.aiPhFL")}
        onResult={(d) => { set("description", d.description || ""); if (d.materials_note) set("materials_note", d.materials_note); }} />
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label={t("docs.number")}><input className="input-flat" value={data.number} onChange={(e) => set("number", e.target.value)} data-testid="fl-number" /></Field>
        <Field label={t("docs.date")}><input type="date" className="input-flat" value={data.date} onChange={(e) => set("date", e.target.value)} data-testid="fl-date" /></Field>
        <Field label={t("docs.client")}><input className="input-flat" value={data.client} onChange={(e) => set("client", e.target.value)} data-testid="fl-client" /></Field>
        <Field label={t("docs.location")}><input className="input-flat" value={data.location} onChange={(e) => set("location", e.target.value)} data-testid="fl-location" /></Field>
        <Field label={t("docs.team")}><input className="input-flat" value={data.team} onChange={(e) => set("team", e.target.value)} placeholder={t("docs.teamPh")} data-testid="fl-team" /></Field>
        <Field label={t("docs.hours")}><input className="input-flat" value={data.hours} onChange={(e) => set("hours", e.target.value)} data-testid="fl-hours" /></Field>
      </div>
      <Field label={t("docs.workDescription")}><textarea rows={4} className="input-flat" value={data.description} onChange={(e) => set("description", e.target.value)} data-testid="fl-description" /></Field>
      <Field label={t("docs.materialsUsed")}><textarea rows={3} className="input-flat" value={data.materials_note} onChange={(e) => set("materials_note", e.target.value)} data-testid="fl-materials" /></Field>
    </div>
  );
}
